import { Gem } from 'lucide-react';
import Image from 'next/image';
import Link from 'next/link';
import React, { useEffect, useState } from 'react';
import clsx from 'clsx';
import { RiHeartAdd2Line } from 'react-icons/ri';
import { TbHeartX } from 'react-icons/tb';

import { Badge } from '@/slw/shared/ui/badge';
import { ListingItemType } from '@/slw/entities/home/home.model';
import { formatCurrency } from '@/slw/shared/lib/format-number';
import { isLoggedInClient } from '@/slw/shared/lib/auth.client';

interface SearchResultCardProps {
  listing: ListingItemType;
}

function SearchResultCardComponent({ listing }: SearchResultCardProps) {
  const normalizeImageUrl = (url: string | undefined): string => {
    if (!url) return '/images/common/no-image-available.jpg';
    
    // Fix malformed URLs like "https:/file.c10depot.com" -> "https://file.c10depot.com"
    let normalized = url;
    if (normalized.startsWith('https:/') && !normalized.startsWith('https://')) {
      normalized = normalized.replace('https:/', 'https://');
    }
    if (normalized.startsWith('http:/') && !normalized.startsWith('http://')) {
      normalized = normalized.replace('http:/', 'http://');
    }
    
    // Fix duplicate base URL
    // Extract base URL (e.g., https://domain.com)
    const baseUrlMatch = normalized.match(/https?:\/\/[^\/]+/);
    if (baseUrlMatch) {
      const baseUrl = baseUrlMatch[0];
      // Remove duplicate base URL pattern (baseUrl/baseUrl/...)
      const duplicatePattern = new RegExp(
        baseUrl.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '\\/' + baseUrl.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'),
        'g'
      );
      normalized = normalized.replace(duplicatePattern, baseUrl + '/');
    }
    
    if (normalized.startsWith('http')) {
      return normalized;
    }

    return '/images/common/no-image-available.jpg';
  };

  const [imgUrl, setUrlImg] = useState<string>(normalizeImageUrl(listing.mainImageUrl));

  const isLogged = isLoggedInClient();

  const renderLocation = () => {
    let location = '';

    if (listing.stateName && !listing.countryName) {
      location = listing.stateName;
    }

    if (!listing.stateName && listing.countryName) {
      location = listing.countryName;
    }

    if (listing.stateName && listing.countryName) {
      location = `${listing.stateName}, ${listing.countryName}`;
    }

    return location;
  };

  useEffect(() => {
    setUrlImg(normalizeImageUrl(listing.mainImageUrl));
  }, [listing.mainImageUrl]);

  return (
    <div
      className='product-item bg-sidebar rounded-[8px] p-2 mb-2 md:mb-4
    hover:[&_img.car-img]:transform-[scale(1.2)]'
    >
      <div className='relative rounded-[6px] overflow-hidden'>
        <div className='w-full aspect-[16/9] relative'>
          <Link href={`/listing/${listing.url}`}>
            <Image
              className='rounded-[6px] w-full transition-all duration-300 object-cover object-center car-img'
              src={imgUrl}
              alt=''
              fill
              onError={() => setUrlImg('/images/common/no-image-available.png')}
            />

            {listing.isPremiumSpotlight && (
              <span
                className='absolute top-3 left-0 bg-[#FF2D55]
           text-white text-sm px-4 py-1 rounded-tr-md rounded-br-md flex items-center gap-2 h-[28px]'
              >
                <Gem size={16} className='inline-block' />
                Premium Spotlight
              </span>
            )}
            {listing.orBestOffer && (
              <span
                className={clsx(
                  // eslint-disable-next-line max-len
                  'absolute top-3 left-0 bg-[#33C7BC] text-white text-sm px-4 py-1 rounded-tr-md rounded-br-md flex items-center gap-2',
                  {
                    'top-[40px]': listing.isPremiumSpotlight,
                  },
                )}
              >
                OBO
              </span>
            )}
          </Link>
        </div>
      </div>

      <div className='flex flex-col gap-2 px-1 pb-1 mt-4'>
        <div className='flex items-center justify-between'>
          <Link href={`/listing/${listing.url}`} className='block font-bold'>
            {listing.subTitle}
          </Link>
          {isLogged && !listing.isFavourite && <RiHeartAdd2Line size={20} className='text-destructive' />}
          {isLogged && listing.isFavourite && <TbHeartX size={20} className='text-destructive' />}
        </div>
        {listing.vehicleEraName && (
          <div>
            <Badge
              variant='outline'
              className='bg-primary text-white rounded-md text-sm border-none leading-[120%] font-normal py-0.75'
            >
              {listing.vehicleEraName}
            </Badge>
          </div>
        )}

        <div className='text-lg font-bold text-primary leading-[160%]'>{formatCurrency(listing.price)}</div>
        <div className='flex items-center justify-between text-sm font-normal leading-[150%]'>
          <span className='flex items-center gap-2'>
            <img src='/images/icon/icon-map-maker.png' alt='' />
            {renderLocation()}
          </span>
          <span>{listing.listingId}</span>
        </div>
      </div>
    </div>
  );
}

export const SearchResultCard = React.memo(SearchResultCardComponent);
